import { Alert } from "@/src/lib/productAlert";
import { supportsMediaPublishing } from "@/src/lib/platformCapabilities";
import { Text } from "@/src/components/ui/BrandText";
import { border, surface, text as uiText } from "@/src/theme/tokens";
import { auth, db, storage } from "@/src/lib/firebase";
import { Ionicons } from "@/src/components/ui/ionicons";
import * as ImagePicker from "expo-image-picker";
import { router } from "expo-router";
import {
  doc,
  serverTimestamp,
  writeBatch,
} from "firebase/firestore";
import { getDownloadURL, ref, uploadBytes } from "firebase/storage";
import React, { useEffect, useMemo, useState } from "react";
import {
  ActivityIndicator,
  Pressable,
  Platform,
  ScrollView,
  View,
} from "react-native";
import { SafeAreaView } from "react-native-safe-area-context";

import { AccountSwitcher } from "../../src/components/AccountSwitcher";
import { PostCard } from "../../src/components/PostCard";
import { PendingPostCard } from "../../src/components/PendingPostCard";
import { useMyPosts } from "../../src/hooks/useMyPosts";
import { usePostPublishQueue } from "../../src/hooks/usePostPublishQueue";
import { useProfile } from "../../src/hooks/useProfile";
import { useActiveAccount } from "../../src/hooks/useActiveAccount";
import { buildPublicProfileData } from "../../src/lib/publicProfiles";
import { subscribeIdentityGroupMemberships } from "../../src/lib/identityGroups";
import { contentMaxWidths } from "../../src/theme/layout";
import {
  getIdentityBadges,
  isStudent,
  isTeacher,
} from "../../src/lib/identity";
import { SUBJECT_LABEL, TRACK_LABEL } from "../../src/lib/taxonomy";

const BG = surface.secondary;
const CARD = surface.primary;
const BORDER = border.subtle;
const MUTED = uiText.muted;
const SETTINGS_ACCESSIBILITY_LABEL = "Paramètres";
const SETTINGS_ACCESSIBILITY_HINT = "Ouvre les paramètres du compte";

type Membership = {
  groupId: string;
  groupName: string;
  role: "owner" | "member" | "admin";
  isPublic: boolean;
};

function safeGroupName(name?: string, groupId?: string) {
  if (name && name.trim().length > 0) return name;
  if (groupId) return `Groupe ${groupId.slice(0, 6)}`;
  return "Groupe";
}

function membershipLabel(role: Membership["role"]) {
  if (role === "owner") return "Propriétaire";
  if (role === "admin") return "Administrateur";
  return "Membre";
}

export default function ProfileScreen() {
  const user = auth.currentUser;
  const { profile: personalProfile } = useProfile(user);
  const {
    activeAccount,
    activeAccountId,
  } = useActiveAccount();
  const profile = activeAccount ?? personalProfile;

  const [groups, setGroups] = useState<Membership[]>([]);
  const [loadingGroups, setLoadingGroups] = useState(false);
  const [section, setSection] = useState<"groups" | "posts">("groups");
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [localPhotoURL, setLocalPhotoURL] = useState("");

  const uid = user?.uid ?? null;
  const {
    items: myPosts,
    loading: loadingPosts,
    error: postsError,
    retry: retryPosts,
    loadMore,
    loadingMore,
    hasMore,
  } = useMyPosts(uid, activeAccountId);
  const {
    jobs: publishJobs,
    reconcileRemote,
    retry: retryPublish,
    cancel: cancelPublish,
    remove: removePublish,
  } = usePostPublishQueue();

  useEffect(() => {
    void reconcileRemote(myPosts.map((post) => ({
      id: post.id,
      authorUid: post.authorUid,
      moderationStatus: (post as any).moderationStatus,
    })));
  }, [myPosts, reconcileRemote]);

  const localPublishJobs = useMemo(() => {
    const remoteIds = new Set(myPosts.map((post) => post.id));
    if (!activeAccountId) return [];
    return publishJobs.filter((job) => {
      const jobIdentityId = "authorIdentityId" in job.author
        ? job.author.authorIdentityId
        : job.authorUid;
      return job.status !== "canceled"
        && !remoteIds.has(job.postId)
        && jobIdentityId === activeAccountId;
    });
  }, [activeAccountId, myPosts, publishJobs]);

  const postsErrorView = postsError ? (
    <View
      style={{
        backgroundColor: surface.danger,
        borderWidth: 1,
        borderColor: border.danger,
        borderRadius: 14,
        padding: 12,
        gap: 8,
      }}
    >
      <Text style={{ color: uiText.danger, fontWeight: "800" }}>{postsError.message}</Text>
      {myPosts.length > 0 ? (
        <Text style={{ color: MUTED, lineHeight: 19 }}>
          Tes dernières publications restent visibles.
        </Text>
      ) : null}
      <Pressable
        onPress={retryPosts}
        disabled={loadingPosts}
        accessibilityRole="button"
        accessibilityLabel="Réessayer de charger mes posts"
        style={{
          alignSelf: "flex-start",
          minHeight: 44,
          justifyContent: "center",
          paddingHorizontal: 10,
          opacity: loadingPosts ? 0.6 : 1,
        }}
      >
        <Text style={{ color: uiText.brand, fontWeight: "900" }}>Réessayer</Text>
      </Pressable>
    </View>
  ) : null;

  useEffect(() => {
    setLocalPhotoURL(activeAccount?.isPersonal
      ? personalProfile?.photoURL ?? activeAccount.photoURL ?? ""
      : "");
  }, [activeAccount?.accountId, activeAccount?.isPersonal, activeAccount?.photoURL, personalProfile?.photoURL]);

  useEffect(() => {
    if (!user || !activeAccountId) {
      setGroups([]);
      setLoadingGroups(false);
      return;
    }

    setLoadingGroups(true);
    return subscribeIdentityGroupMemberships(
      activeAccountId,
      (items) => {
        setGroups(
          items.map((item) => ({
            groupId: item.groupId,
            groupName: safeGroupName(item.groupName, item.groupId),
            role: item.role,
            isPublic: item.isPublic,
          }))
        );
        setLoadingGroups(false);
      },
      () => {
        setGroups([]);
        setLoadingGroups(false);
      }
    );
  }, [activeAccountId, user]);

  async function updateTeacherPhoto() {
    if (!supportsMediaPublishing) return;
    const currentUser = auth.currentUser;
    if (!currentUser) return;

    if (!activeAccount?.isPersonal || !isTeacher(activeAccount)) {
      Alert.alert("Indisponible", "La photo est gérée depuis le profil personnel du professeur.");
      return;
    }

    try {
      const permission = await ImagePicker.requestMediaLibraryPermissionsAsync();
      if (!permission.granted) {
        Alert.alert(
          "Permission requise",
          "Autorise l'acces a la galerie pour choisir une photo de profil."
        );
        return;
      }

      const result = await ImagePicker.launchImageLibraryAsync({
        mediaTypes: ["images"],
        allowsEditing: true,
        aspect: [1, 1],
        quality: 0.7,
      });

      if (result.canceled || !result.assets?.length) return;

      setUploadingPhoto(true);

      const asset = result.assets[0];
      const response = await fetch(asset.uri);
      const blob = await response.blob();
      const storageRef = ref(storage, `profile_photos/${currentUser.uid}.jpg`);

      await uploadBytes(storageRef, blob, {
        contentType: "image/jpeg",
      });

      const downloadURL = await getDownloadURL(storageRef);

      const batch = writeBatch(db);
      const userRef = doc(db, "users", currentUser.uid);
      const publicProfileRef = doc(db, "publicProfiles", currentUser.uid);

      batch.update(userRef, {
        photoURL: downloadURL,
        updatedAt: serverTimestamp(),
      });

      batch.set(
        publicProfileRef,
        buildPublicProfileData(
          currentUser.uid,
          {
            ...personalProfile,
            photoURL: downloadURL,
          },
          { includeCreatedAt: true }
        ),
        { merge: true }
      );

      await batch.commit();

      setLocalPhotoURL(downloadURL);
      Alert.alert("Photo mise à jour", "Ta photo de profil a bien été enregistrée.");
    } catch (e: any) {
      if (__DEV__) console.warn("[profile] photo update failed", e?.code ?? "unknown");
      Alert.alert(
        "Photo non mise à jour",
        "La photo n'a pas pu être enregistrée. Vérifie ta connexion et réessaie."
      );
    } finally {
      setUploadingPhoto(false);
    }
  }

  return (
    <SafeAreaView edges={["top", "left", "right"]} style={{ flex: 1, backgroundColor: BG }}>
      <ScrollView
        contentContainerStyle={{
          alignSelf: "center",
          maxWidth: contentMaxWidths.profile,
          width: "100%",
          paddingHorizontal: 16,
          paddingTop: 12,
          paddingBottom: 24,
          gap: 12,
        }}
        showsVerticalScrollIndicator={Platform.OS === "web"}
      >
        <View style={{ flexDirection: "row", justifyContent: "flex-end", alignItems: "center" }}>
          <Pressable
            onPress={() => router.push("/settings")}
            accessibilityRole="button"
            accessibilityLabel={SETTINGS_ACCESSIBILITY_LABEL}
            accessibilityHint={SETTINGS_ACCESSIBILITY_HINT}
            hitSlop={6}
            style={{
              width: 48,
              height: 48,
              borderRadius: 14,
              backgroundColor: CARD,
              borderWidth: 1,
              borderColor: BORDER,
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Ionicons name="settings-outline" size={18} color={uiText.primary} />
          </Pressable>
        </View>

        <View style={{ backgroundColor: CARD, borderRadius: 18, padding: 14, borderWidth: 1, borderColor: BORDER, gap: 12 }}>
          <AccountSwitcher label="" />



          <View style={{ flexDirection: "row", gap: 8, flexWrap: "wrap" }}>
            {getIdentityBadges(profile).slice(1).map((badge) => (
              <View key={badge.kind} style={{ paddingHorizontal: 10, paddingVertical: 6, borderRadius: 999, backgroundColor: surface.overlay, borderWidth: 1, borderColor: BORDER }}>
                <Text style={{ color: uiText.secondary, fontWeight: "800" }}>{badge.label}</Text>
              </View>
            ))}

            {isStudent(profile) && profile?.track ? (
              <View style={{ paddingHorizontal: 10, paddingVertical: 6, borderRadius: 999, backgroundColor: surface.overlay, borderWidth: 1, borderColor: BORDER }}>
                <Text style={{ color: uiText.secondary, fontWeight: "800" }}>
                  {TRACK_LABEL[profile.track as keyof typeof TRACK_LABEL] ?? profile.track}
                </Text>
              </View>
            ) : null}

            {isTeacher(profile) && profile?.mainSubject ? (
              <View style={{ paddingHorizontal: 10, paddingVertical: 6, borderRadius: 999, backgroundColor: surface.overlay, borderWidth: 1, borderColor: BORDER }}>
                <Text style={{ color: uiText.secondary, fontWeight: "800" }}>
                  {SUBJECT_LABEL[profile.mainSubject as keyof typeof SUBJECT_LABEL] ?? profile.mainSubject}
                </Text>
              </View>
            ) : null}
          </View>

          {activeAccount?.isPersonal && isTeacher(activeAccount) && supportsMediaPublishing ? (
            <Pressable
              onPress={updateTeacherPhoto}
              disabled={uploadingPhoto}
              style={{
                alignSelf: "flex-start",
                paddingHorizontal: 12,
                height: 36,
                borderRadius: 999,
                backgroundColor: surface.selected,
                borderWidth: 1,
                borderColor: border.brand,
                alignItems: "center",
                justifyContent: "center",
                opacity: uploadingPhoto ? 0.7 : 1,
              }}
            >
              {uploadingPhoto ? (
                <ActivityIndicator color={uiText.primary} size="small" />
              ) : (
                <Text style={{ color: uiText.primary, fontWeight: "900", fontSize: 12 }}>
                  {localPhotoURL ? "Mettre à jour la photo" : "Ajouter une photo"}
                </Text>
              )}
            </Pressable>
          ) : null}
        </View>

        <View
          style={{
            borderWidth: 1,
            borderColor: border.subtle,
            backgroundColor: surface.overlay,
            padding: 14,
            borderRadius: 16,
          }}
        >
          <View
            style={{
              flexDirection: "row",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <View style={{ flexDirection: "row", gap: 10 }}>
              <Pressable
                accessibilityRole="tab" accessibilityState={{ selected: section === "groups" }} onPress={() => setSection("groups")}
                style={{
                  paddingVertical: 8,
                  paddingHorizontal: 12,
                  borderRadius: 999,
                  borderWidth: 1,
                  borderColor:
                    section === "groups"
                      ? surface.selected
                      : surface.elevated,
                  backgroundColor:
                    section === "groups"
                      ? surface.selected
                      : surface.overlay,
                }}
              >
                <Text style={{ color: uiText.primary, fontWeight: "900", fontSize: 14 }}>
                  {section === "groups" ? "✓ " : ""}Mes groupes
                </Text>
              </Pressable>

              <Pressable
                accessibilityRole="tab" accessibilityState={{ selected: section === "posts" }} onPress={() => setSection("posts")}
                style={{
                  paddingVertical: 8,
                  paddingHorizontal: 12,
                  borderRadius: 999,
                  borderWidth: 1,
                  borderColor:
                    section === "posts"
                      ? surface.selected
                      : surface.elevated,
                  backgroundColor:
                    section === "posts"
                      ? surface.selected
                      : surface.overlay,
                }}
              >
                <Text style={{ color: uiText.primary, fontWeight: "900", fontSize: 14 }}>
                  {section === "posts" ? "✓ " : ""}Mes posts
                </Text>
              </Pressable>
            </View>

            <Text style={{ color: MUTED, fontWeight: "800" }}>
              {section === "groups"
                ? loadingGroups
                  ? "..."
                  : String(groups.length)
                : loadingPosts
                  ? "..."
                  : postsError && myPosts.length === 0 && localPublishJobs.length === 0
                    ? "—"
                    : String(myPosts.length + localPublishJobs.length)}
            </Text>
          </View>

          <View style={{ height: 12 }} />

          {section === "groups" ? (
            loadingGroups ? (
              <Text style={{ color: MUTED }}>Chargement...</Text>
            ) : groups.length === 0 ? (
              <Text style={{ color: MUTED }}>Aucun groupe pour le moment.</Text>
            ) : (
              <View style={{ gap: 10 }}>
                {groups.map((g) => (
                  <Pressable
                    key={g.groupId}
                    onPress={() => router.push(`/group/${g.groupId}`)}
                    style={{
                      borderWidth: 1,
                      borderColor: border.subtle,
                      backgroundColor: surface.overlay,
                      padding: 12,
                      borderRadius: 14,
                    }}
                  >
                    <Text style={{ color: uiText.primary, fontWeight: "900" }}>
                      {safeGroupName(g.groupName, g.groupId)}
                    </Text>

                    <Text style={{ color: MUTED, marginTop: 2 }}>
                      {membershipLabel(g.role)} · {g.isPublic ? "Public" : "Privé"}
                    </Text>
                  </Pressable>
                ))}

                {groups.length > 8 && (
                  <Text style={{ color: MUTED }}>+{groups.length - 8} autres...</Text>
                )}
              </View>
            )
          ) : loadingPosts ? (
            <Text style={{ color: MUTED }}>Chargement...</Text>
          ) : postsError && myPosts.length === 0 && localPublishJobs.length === 0 ? (
            postsErrorView
          ) : myPosts.length === 0 && localPublishJobs.length === 0 ? (
            <Text style={{ color: MUTED }}>Aucun post pour le moment.</Text>
          ) : (
            <View style={{ gap: 10 }}>
              {postsErrorView}
              {localPublishJobs.length > 0 ? (
                <View style={{ gap: 10 }}>
                  <View style={{ gap: 3 }}>
                    <Text style={{ color: uiText.primary, fontWeight: "900", fontSize: 18 }}>Publications en cours</Text>
                    <Text style={{ color: MUTED, lineHeight: 19 }}>
                      Ces posts restent privés jusqu&apos;à la fin de l&apos;envoi et de la validation.
                    </Text>
                  </View>
                  {localPublishJobs.map((job) => (
                    <PendingPostCard
                      key={job.jobId}
                      job={job}
                      onRetry={() => void retryPublish(job.jobId)}
                      onCancel={() => void cancelPublish(job.jobId)}
                      onRemove={() => void removePublish(job.jobId)}
                    />
                  ))}
                </View>
              ) : null}
              {myPosts.map((p) => {
                const likedByArr: string[] = Array.isArray((p as any).likedBy)
                  ? (p as any).likedBy
                  : [];

                const liked = !!user && likedByArr.includes(user.uid);
                const likes = Number.isInteger((p as any).likesCount)
                  ? (p as any).likesCount
                  : likedByArr.length;

                return (
                  <PostCard
                    key={p.id}
                    item={{
                      ...p,
                      subjectLabel: p.subject
                        ? SUBJECT_LABEL[p.subject as keyof typeof SUBJECT_LABEL] ?? p.subject
                        : "Général",
                    }}
                    liked={liked}
                    likes={likes}
                    isMine={true}
                    onToggleLike={async () => {
                      router.push(`/post/${p.id}`);
                    }}
                    onOpenActions={() => {
                      router.push(`/post/${p.id}`);
                    }}
                    disableAuthorLink
                    onSharePost={(post) => {
                      router.push({
                        pathname: "/share/post/[postId]",
                        params: { postId: post.id },
                      });
                    }}
                  />
                );
              })}

              {hasMore && (
                <Pressable
                  onPress={() => loadMore()}
                  disabled={loadingMore}
                  style={{
                    marginTop: 6,
                    alignSelf: "flex-start",
                    paddingVertical: 10,
                    paddingHorizontal: 12,
                    borderRadius: 12,
                    borderWidth: 1,
                    borderColor: border.subtle,
                    backgroundColor: surface.overlay,
                    opacity: loadingMore ? 0.6 : 1,
                  }}
                >
                  <Text style={{ color: uiText.primary, fontWeight: "900" }}>
                    {loadingMore ? "Chargement..." : "Voir plus"}
                  </Text>
                </Pressable>
              )}
            </View>
          )}
        </View>

      </ScrollView>
    </SafeAreaView>
  );
}
